// src/app/core/error-handler/services/error-mapping-registry.service.ts
import { inject, Injectable } from '@angular/core';
import type { ErrorMapping, ErrorMappingWithRule } from '../../models/error-mapping.model';
import { AuthErrorMappingService } from './mappings/auth-error-mapping.service';
import { TranslateService } from '@ngx-translate/core';
import type { AppErrorOptions } from '../../models/error.model';
import { AppError } from '../../models/error.model';
import { PersonErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/person-error-mapping.service';
import { CensusLineErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/census-line-error-mapping.service';
import { TelemedicineErrorMappingService } from './mappings/telemedicine-error-mapping.service';
import { ProfileErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/profile-error-mapping.service';
import { FeedbackErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/feedback-error-mapping.service';
import { ClaimsErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/claims-error-mapping.service';
import { FaqErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/faq-error-mapping.service';
import { UploadErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/upload-error-mapping.service';
import { WalletErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/wallet-error-mapping.service';
import { BankAccountErrorMappingService } from '@core/error-handler/services/error-mappings/mappings/bank-account-error-mapping.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorMappingRegistryService {
  private translate = inject(TranslateService);

  // Default error messages for general HTTP status codes
  private defaultMessages: { [status: number]: string } = {
    400: 'badRequest',
    401: 'unauthorized',
    403: 'forbidden',
    404: 'notFound',
    408: 'timeout',
    429: 'tooManyRequests',
    500: 'serverError',
    503: 'serviceUnavailable',
    0: 'No Connection'
  };

  // Default error handling options
  private defaultOptions: AppErrorOptions = {
    duration: 10000,
    showInInterceptor: true
  };

  private mappingServices: ErrorMapping[];

  private authErrorMapping = inject(AuthErrorMappingService);
  private personErrorMapping = inject(PersonErrorMappingService);
  private censusLineErrorMapping = inject(CensusLineErrorMappingService);
  private telemedicineErrorMapping = inject(TelemedicineErrorMappingService);
  private profileErrorMapping = inject(ProfileErrorMappingService);
  private feedbackErrorMapping = inject(FeedbackErrorMappingService);
  private claimsErrorMapping = inject(ClaimsErrorMappingService);
  private faqErrorMapping = inject(FaqErrorMappingService);
  private uploadErrorMapping = inject(UploadErrorMappingService);
  private walletErrorMapping = inject(WalletErrorMappingService);
  private bankAccountErrorMapping = inject(BankAccountErrorMappingService);

  constructor() {
    // Register all domain-specific error mappings
    this.mappingServices = [
      this.authErrorMapping,
      this.personErrorMapping,
      this.censusLineErrorMapping,
      this.telemedicineErrorMapping,
      this.profileErrorMapping,
      this.feedbackErrorMapping,
      this.claimsErrorMapping,
      this.faqErrorMapping,
      this.uploadErrorMapping,
      this.walletErrorMapping,
      this.bankAccountErrorMapping
    ];
  }

  /**
   * Find the appropriate domain-specific mapping service for a URL
   */
  private findMappingService(url: string): ErrorMapping | null {
    return this.mappingServices.find((service) => service.appliesTo(url)) || null;
  }

  /**
   * Get the appropriate error for a given request
   */
  getErrorForRequest(url: string, status: number): AppError {
    const mappingService = this.findMappingService(url);

    if (!mappingService) {
      // No specific mapping found, use default message
      const message = this.translate.instant(
        this.defaultMessages[status] || this.defaultMessages[0]
      );
      return new AppError(message, { ...this.defaultOptions, status });
    }

    // If we have a specific mapping service for this URL, use it
    if ('getMatchingRule' in mappingService) {
      const rule = (mappingService as ErrorMappingWithRule).getMatchingRule(url);

      if (rule) {
        const messageKey =
          rule.messages[status] || this.defaultMessages[status] || this.defaultMessages[0];
        const message = this.translate.instant(messageKey);

        // Merge default options with rule-specific options
        const options: AppErrorOptions = {
          status,
          ...this.defaultOptions,
          ...(rule.options || {})
        };

        return new AppError(message, options);
      }
    }

    // Fallback to default if no matching rule found
    const message = this.translate.instant(this.defaultMessages[status] || this.defaultMessages[0]);
    return new AppError(message, { ...this.defaultOptions, status });
  }

  /**
   * Get error for successful responses that indicate application-level failures
   * @param url The API URL
   * @param errorCode Optional error code from the response
   * @param errorMessage Error message from the response (fallback)
   */
  getErrorForSuccessResponse(
    url: string,
    errorCode?: string | null,
    errorMessage?: string | null
  ): AppError {
    const mappingService = this.findMappingService(url);
    if (!mappingService || !('getMatchingRule' in mappingService)) {
      // No specific mapping found, use the provided error message or a default
      const message = errorMessage || this.translate.instant('errors.operationFailed');
      return new AppError(message, { ...this.defaultOptions, status: 200 });
    }

    const rule = (mappingService as ErrorMappingWithRule).getMatchingRule(url);

    if (!rule || !rule.messages.success) {
      // No success-specific error mapping, use the provided message
      const message = errorMessage || this.translate.instant('errors.operationFailed');
      return new AppError(message, { ...this.defaultOptions, status: 200 });
    }

    // Try to find a specific error code mapping
    let messageKey: string | undefined;

    if (errorCode && rule.messages.success[errorCode]) {
      // We have a specific message for this error code
      messageKey = rule.messages.success[errorCode];
    } else if (rule.messages.success.default) {
      // Use the default success error message
      messageKey = rule.messages.success.default;
    }

    // If we found a message key, translate it, otherwise use the provided error message
    const message = messageKey
      ? this.translate.instant(messageKey)
      : errorMessage || this.translate.instant('errors.operationFailed');

    // Merge default options with rule-specific options
    const options = {
      status: 200,
      ...this.defaultOptions,
      ...(rule.options || {})
    };

    return new AppError(message, options);
  }

  /**
   * Register a new error mapping service
   */
  registerMappingService(service: ErrorMapping) {
    this.mappingServices.push(service);
  }
}
